@if (loading()) {
  <div class="flex items-center justify-center h-full">
    <mat-spinner diameter="40" />
  </div>
} @else {
  <div class="container flex flex-col mx-auto px-4 md:px-6 pt-8 pb-4">
    <!-- Header -->
    <div class="flex items-center gap-3 mb-6 min-h-10">
      <button class="inline-flex!" mat-icon-button (click)="navigateBack()">
        <mat-icon>arrow_back</mat-icon>
      </button>

      <h2 class="text-2xl font-medium">Rechten matrix</h2>
    </div>

    <!-- Permissions matrix card -->
    <div class="bg-white rounded-lg">
      <div
        class="flex items-center justify-between px-6 py-3 border-b border-gray-200"
      >
        <div class="flex items-center gap-2">
          <h3 class="text-lg font-medium text-gray-700">Alle rollen</h3>
          <span class="text-sm text-gray-400">
            ({{ roles().length }} rollen)
          </span>
        </div>

        <div
          class="inline-flex items-center border border-(--mat-sys-outline) rounded-sm p-1 h-12"
        >
          <button
            class="inline-flex items-center justify-center h-full whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-gray-700 hover:bg-gray-100 hover:text-gray-900 hover:cursor-pointer focus-visible:ring-gray-600 rounded-xs px-2 h-8 text-xs font-medium"
            (click)="expandAll()"
            type="button"
          >
            Alles uitklappen
          </button>

          <div class="w-px h-4 bg-(--mat-sys-outline) mx-1"></div>

          <button
            class="inline-flex items-center justify-center h-full whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-gray-700 hover:bg-gray-100 hover:text-gray-900 hover:cursor-pointer focus-visible:ring-gray-600 rounded-xs px-2 h-8 text-xs font-medium"
            (click)="collapseAll()"
            type="button"
          >
            Alles inklappen
          </button>
        </div>
      </div>

      @if (roles().length === 0) {
        <div class="flex flex-col items-center justify-center py-20 text-center">
          <div
            class="size-20 flex items-center justify-center rounded-full mx-auto bg-gray-100 text-gray-400"
          >
            <mat-icon>shield</mat-icon>
          </div>

          <div class="text-sm text-gray-500 py-4 text-center">
            Geen rollen gevonden.
          </div>
        </div>
      } @else {
        <!-- Permissions matrix table -->
        <div class="p-6">
          <div class="border border-gray-200 rounded-lg overflow-x-auto">
            <table
              class="w-full border-separate border-spacing-0"
              style="table-layout: fixed"
              [style.min-width.px]="minTableWidth()"
            >
              <colgroup>
                <col style="width: 280px" />
                @for (role of roles(); track role._id) {
                  <col />
                }
              </colgroup>
              <thead>
                <tr>
                  <th
                    class="text-left text-sm font-semibold text-gray-500 py-3 px-4 sticky left-0 bg-white z-10 border-r border-b border-gray-200"
                  >
                    Rechten
                  </th>
                  @for (
                    role of roles();
                    track role._id;
                    let first = $first
                  ) {
                    <th
                      class="text-center text-xs font-semibold text-gray-500 py-3 px-4 border-b border-gray-200"
                      [class.border-l]="!first"
                    >
                      <div class="flex flex-col items-center gap-0.5">
                        <span
                          class="text-gray-700 normal-case text-sm font-semibold"
                        >
                          {{ role.label }}
                        </span>
                        <span
                          class="text-gray-400 normal-case text-xs font-normal"
                        >
                          {{ rolePermissionCount(role._id) }}/{{
                            totalPermissionCount
                          }}
                        </span>
                      </div>
                    </th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (group of permissionGroups(); track group.key; let lastGroup = $last) {
                  <!-- Group header row -->
                  <tr
                    class="bg-gray-50 cursor-pointer select-none hover:bg-gray-100 transition-colors"
                    (click)="toggleGroupExpanded(group.key)"
                  >
                    <td
                      class="py-2.5 px-4 sticky left-0 bg-inherit z-10"
                      [class.border-b]="!(lastGroup && !isGroupExpanded(group.key))"
                      [class.border-gray-200]="!(lastGroup && !isGroupExpanded(group.key))"
                    >
                      <div class="flex items-center gap-1">
                        <div
                          class="transition-transform"
                          [class]="{
                            '-rotate-90': !isGroupExpanded(group.key),
                          }"
                        >
                          <mat-icon
                            class="flex-none text-gray-500 text-xl/5! py-1 px-0.5"
                          >
                            arrow_drop_down
                          </mat-icon>
                        </div>
                        <span class="text-sm font-semibold text-gray-700">
                          {{ group.label }}
                        </span>
                      </div>
                    </td>
                    @for (role of roles(); track role._id) {
                      <td
                        class="bg-inherit"
                        [class.border-b]="!(lastGroup && !isGroupExpanded(group.key))"
                        [class.border-gray-200]="!(lastGroup && !isGroupExpanded(group.key))"
                      ></td>
                    }
                  </tr>

                  <!-- Permission rows (animated expand/collapse) -->
                  <tr>
                    <td [attr.colspan]="tableColspan()" class="p-0!">
                      <div
                        [@expandCollapse]="
                          isGroupExpanded(group.key) ? 'expanded' : 'collapsed'
                        "
                      >
                        @for (
                          permission of group.permissions;
                          track permission.key;
                          let last = $last
                        ) {
                          <div
                            class="grid border-b"
                            [class]="
                              last && lastGroup ? 'border-transparent' : last ? 'border-gray-200' : 'border-gray-100'
                            "
                            [style.grid-template-columns]="
                              gridTemplateColumns()
                            "
                            [style.min-width.px]="minTableWidth()"
                          >
                            <div
                              class="py-3 px-4 pl-11 sticky left-0 bg-white z-10 border-r border-gray-200"
                            >
                              <div class="flex flex-col">
                                <span
                                  class="text-sm font-medium text-gray-800"
                                >
                                  {{ permission.actionLabel }}
                                </span>
                                <span class="text-xs text-gray-400">
                                  {{ group.label }}
                                  {{ permission.actionLabel | lowercase }}
                                </span>
                              </div>
                            </div>
                            @for (
                              role of roles();
                              track role._id;
                              let first = $first
                            ) {
                              <div
                                class="py-3 px-4 text-center flex items-center justify-center"
                                [class.border-l]="!first"
                                [class.border-gray-200]="!first"
                              >
                                @if (
                                  roleHasPermission(
                                    role._id,
                                    permission.value
                                  )
                                ) {
                                  <mat-icon class="text-green-600! text-xl!">
                                    check
                                  </mat-icon>
                                } @else {
                                  <span
                                    class="text-gray-300 text-lg font-medium"
                                  >
                                    —
                                  </span>
                                }
                              </div>
                            }
                          </div>
                        }
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      }
    </div>
  </div>
}
